import { motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import Select from "react-select";
import { apiCall } from "../service/apiService";
import { CREATE_SKILLS, GET_SETUP } from "../service/Constant";
import Header from "./Header";
import PageLoader from "./PageLoader";
import Sidebar from "./Sidebar";

export default function Dropdowns() {
  /* ================= STATE ================= */
  const [departments, setDepartments] = useState([]);
  const [selectedDept, setSelectedDept] = useState("");

  const [competencies, setCompetencies] = useState([]);
  const [skills, setSkills] = useState([]);
  const [clinicalCases, setClinicalCases] = useState([]);

  const [loading, setLoading] = useState(true);

  /* ===== ADD SKILL MODAL ===== */
  const [showModal, setShowModal] = useState(false);
  const [skillFor, setSkillFor] = useState(""); // diag | proc
  const [skillTitle, setSkillTitle] = useState("");
  const [selectedCompetency, setSelectedCompetency] = useState(null);

  /* ================= FETCH SETUP ================= */
  const fetchSetup = async () => {
    setLoading(true);
    const res = await apiCall("", GET_SETUP, "GET");

    if (res?.status) {
      setDepartments(res.departments || []);
      setCompetencies(res.comp || []);
      setSkills(res.skills || []);
      setClinicalCases(res.clinicalCases || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSetup();
  }, []);

  /* ================= FILTERS ================= */
  const diagnosticCompetencies = useMemo(
    () =>
      competencies.filter(
        c => c.departmentId === selectedDept && c.for === "diag"
      ),
    [competencies, selectedDept]
  );

  const proceduralCompetencies = useMemo(
    () =>
      competencies.filter(
        c => c.departmentId === selectedDept && c.for === "proc"
      ),
    [competencies, selectedDept]
  );

  const skillsMap = useMemo(() => {
    const map = {};
    skills.forEach(skill => {
      const key = skill.competency?._id || skill.computencyId;
      if (!map[key]) map[key] = [];
      map[key].push(skill);
    });
    return map;
  }, [skills]);

  /* ================= MODAL ================= */
  const openAddSkillModal = (type) => {
    setSkillFor(type);
    setSkillTitle("");
    setSelectedCompetency(null);
    setShowModal(true);
  };

  const handleCreateSkill = async () => {
    if (!skillTitle || !selectedCompetency) {
      toast.error("All fields are required");
      return;
    }

     const payload = { title: skillTitle, computencyId: selectedCompetency.value, for: skillFor };
    const res = await apiCall(payload, CREATE_SKILLS, "POST");

    if (res?.status) {
      setSkills(prev => [res.skill, ...prev]);
      toast.success("Skill added successfully");
      setShowModal(false);
    } else {
      toast.error("Failed to add skill");
    }
  };

  /* ================= UI ================= */
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col mt-[100px]">
      <Header />
      <div className="flex flex-1">
        <Sidebar />

        <div className="flex-1 p-6 md:p-8 ml-0 md:ml-64 space-y-6">

          {/* ===== Department Dropdown ===== */}
          <div className="bg-white p-4 rounded-xl shadow">
            <label className="text-sm font-medium text-gray-600">
              Select Department
            </label>
            <select
              className="mt-2 w-full border rounded-lg p-2"
              value={selectedDept}
              onChange={(e) => setSelectedDept(e.target.value)}
            >
              <option value="">-- Select Department --</option>
              {departments.map(dep => (
                <option key={dep._id} value={dep._id}>
                  {dep.name}
                </option>
              ))}
            </select>
          </div>

          {loading && 
            <PageLoader />
           }

          {!loading && selectedDept && (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

              <SkillTable
                title="Diagnostic Skills"
                color="blue"
                competencies={diagnosticCompetencies}
                skillsMap={skillsMap}
                onAdd={() => openAddSkillModal("diag")}
              />

              <SkillTable
                title="Procedural Skills"
                color="purple"
                competencies={proceduralCompetencies}
                skillsMap={skillsMap}
                onAdd={() => openAddSkillModal("proc")}
              />

            </div>
          )}
        </div>
      </div>

      {/* ================= ADD SKILL MODAL ================= */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white w-[420px] rounded-xl p-6 space-y-4">

            <h3 className="text-lg font-semibold">
              Add {skillFor === "diag" ? "Diagnostic" : "Procedural"} Skill
            </h3>

            <Select
              placeholder="Select Competency"
              value={selectedCompetency}
              onChange={setSelectedCompetency}
              options={competencies
                .filter(
                  c =>
                    c.for === skillFor &&
                    c.departmentId === selectedDept
                )
                .map(c => ({
                  value: c._id,
                  label: c.title,
                }))
              }
            />

            <input
              className="w-full border rounded-lg px-3 py-2"
              placeholder="Skill title"
              value={skillTitle}
              onChange={(e) => setSkillTitle(e.target.value)}
            />

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-1 border rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateSkill}
                className="px-4 py-1 bg-indigo-600 text-white rounded-lg"
              >
                Save
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}

/* ================= SKILL TABLE ================= */
function SkillTable({ title, color, competencies, skillsMap, onAdd }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow p-4"
    >
      <div className="flex justify-between items-center mb-4">
        <h3 className={`text-lg font-semibold text-${color}-600`}>
          {title}
        </h3>
        <button
          onClick={onAdd}
          className="text-xs px-3 py-1 rounded-lg bg-indigo-600 text-white"
        >
          + Add Skill
        </button>
      </div>

      {competencies.length ? competencies.map(comp => (
        <div key={comp._id} className="mb-4 border rounded-lg">
          <div className="px-3 py-2 font-semibold bg-slate-100">
            {comp.title}
            <span className="inline-block mt-1 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-purple-100 text-purple-700" > Global as per PGMEB's curriculum </span>
          </div>

          <ul className="p-3 space-y-2 text-sm">
            {skillsMap[comp._id]?.length ? (
              skillsMap[comp._id].map(skill => (
                <li
                  key={skill._id}
                  className="flex justify-between items-center rounded-lg px-3 py-2 hover:bg-slate-50"
                >
                  <div>
                    <div className="font-medium">{skill.title}</div>

                    <span
                      className={`inline-block mt-1 px-2 py-0.5 text-[10px] font-semibold rounded-full
                        ${
                          skill.addedBy?.role === "staff"
                            ? "bg-purple-100 text-purple-700"
                            : skill.addedBy?.role === "academic"
                            ? "bg-amber-100 text-amber-700"
                            : skill.addedBy?.role === "user"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-slate-200 text-slate-700"
                        }`}
                    >
                      {skill.addedBy?._id
                        ? `Added by ${skill.addedBy.name} (${skill.addedBy.role})`
                        : "🌍 Global by Super Admin"}
                    </span>
                  </div>

                  {/* <span
                    className={`text-xs px-2 py-1 rounded-full ${
                      skill.status
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {skill.status ? "Active" : "Inactive"}
                  </span> */}
                </li>
              ))
            ) : (
              <li className="text-center text-gray-400 italic">
                No skills added
              </li>
            )}
          </ul>
        </div>
      )) : (
        <p className="text-gray-400 italic">
          No competencies found
        </p>
      )}
    </motion.div>
  );
}
